import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, Loader2, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { api, track, fileUrl } from "../lib/api";
import { CAR_BRANDS } from "../data/site";
import { toast } from "sonner";

const YEARS = Array.from({ length: new Date().getFullYear() - 1969 }, (_, i) => String(new Date().getFullYear() - i));
const STEPS = ["Vehicle", "Condition", "Contact"];

const YN = ({ name, value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {["Yes", "No"].map((o) => (
      <button key={o} type="button" onClick={() => onChange(name, o)}
        className={`flex-1 rounded-xl border-2 py-3 font-bold text-sm transition-all ${value === o ? "border-brand-navy bg-brand-navy text-white" : "border-brand-line bg-white text-brand-navy hover:border-brand-navy/40"}`}
        data-testid={`${testid}-${o.toLowerCase()}`}>
        {o}
      </button>
    ))}
  </div>
);

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wide text-brand-muted mb-1.5">
      {label} {required && <span className="text-brand-gold">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs font-semibold text-red-600" data-testid="field-error">{error}</p>}
  </div>
);

const inputCls = "w-full rounded-xl border-2 border-brand-line bg-white px-4 py-3 text-base font-medium text-brand-navy outline-none transition-colors focus:border-brand-gold";

export default function QuoteWizard({ compact = false }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    brand: "", model: "", year: "", rego: "", kilometres: "",
    on_finance: "", accident: "", written_off: "", condition: "", service_logbook: "", service_history: "",
    name: "", email: "", phone: "", suburb: "", preferred_time: "",
    photos: [], company: "",
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const started = useRef(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!started.current) { started.current = true; track("form_start", { source: compact ? "hero" : "page" }); }
  }, [compact]);

  const set = (k, v) => { setData((d) => ({ ...d, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = (s) => {
    const e = {};
    if (s === 0) {
      if (!data.brand) e.brand = "Select a brand";
      if (!data.model.trim()) e.model = "Enter the model";
      if (!data.year) e.year = "Select a year";
    }
    if (s === 1) {
      ["on_finance", "accident", "written_off", "condition", "service_history"].forEach((k) => { if (!data[k]) e[k] = "Required"; });
    }
    if (s === 2) {
      if (!data.name.trim()) e.name = "Enter your name";
      if (!data.phone.trim()) e.phone = "Enter your phone";
      if (data.email && !/^[^@]+@[^@]+\.[^@]+$/.test(data.email)) e.email = "Enter a valid email";
      if (!data.suburb.trim()) e.suburb = "Enter your suburb or postcode";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validate(step)) return;
    track("form_step", { step: String(step + 2) });
    setStep((s) => Math.min(s + 1, 2));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onFiles = async (files) => {
    const list = Array.from(files).slice(0, 6 - data.photos.length);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const { data: res } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setData((d) => ({ ...d, photos: [...d.photos, res.url] }));
      }
    } catch (err) {
      toast.error("Could not upload one or more photos.");
    } finally { setUploading(false); }
  };

  const removePhoto = (url) => setData((d) => ({ ...d, photos: d.photos.filter((p) => p !== url) }));

  const submit = async () => {
    if (!validate(2)) return;
    setSubmitting(true);
    try {
      await api.post("/leads", { ...data, source: compact ? "hero_form" : "quote_form" });
      navigate("/thank-you");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call us on 0431 206 206.");
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white p-5 sm:p-7 shadow-2xl shadow-brand-navy/10 border border-brand-line" data-testid="quote-wizard">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-xl font-extrabold text-brand-navy">Get a price for your car</h3>
        <span className="text-xs font-bold text-brand-muted" data-testid="wizard-step-indicator">Step {step + 1} of 3</span>
      </div>
      {/* progress */}
      <div className="flex gap-1.5 mb-6" data-testid="wizard-progress">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-brand-gold" : "bg-brand-line"}`} />
            <span className={`mt-1 block text-[10px] font-bold uppercase tracking-wide ${i <= step ? "text-brand-navy" : "text-brand-muted"}`}>{s}</span>
          </div>
        ))}
      </div>

      {/* honeypot */}
      <input type="text" tabIndex={-1} autoComplete="off" value={data.company} onChange={(e) => set("company", e.target.value)} className="hidden" aria-hidden="true" />

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }} className="space-y-4">
          {step === 0 && (
            <>
              <Field label="Brand" required error={errors.brand}>
                <select value={data.brand} onChange={(e) => set("brand", e.target.value)} className={inputCls} data-testid="input-brand">
                  <option value="">Select brand</option>
                  {CAR_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Model" required error={errors.model}>
                  <input value={data.model} onChange={(e) => set("model", e.target.value)} placeholder="e.g. Corolla" className={inputCls} data-testid="input-model" />
                </Field>
                <Field label="Year" required error={errors.year}>
                  <select value={data.year} onChange={(e) => set("year", e.target.value)} className={inputCls} data-testid="input-year">
                    <option value="">Year</option>
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Rego (optional)"><input value={data.rego} onChange={(e) => set("rego", e.target.value)} placeholder="ABC123" className={inputCls} data-testid="input-rego" /></Field>
                <Field label="Kilometres"><input value={data.kilometres} onChange={(e) => set("kilometres", e.target.value)} placeholder="e.g. 145000" inputMode="numeric" className={inputCls} data-testid="input-km" /></Field>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <Field label="Currently on finance?" required error={errors.on_finance}><YN name="on_finance" value={data.on_finance} onChange={set} testid="yn-finance" /></Field>
              <Field label="Ever in an accident?" required error={errors.accident}><YN name="accident" value={data.accident} onChange={set} testid="yn-accident" /></Field>
              <Field label="On the written-off register?" required error={errors.written_off}><YN name="written_off" value={data.written_off} onChange={set} testid="yn-writtenoff" /></Field>
              <Field label="Condition" required error={errors.condition}>
                <div className="grid grid-cols-3 gap-2" data-testid="input-condition">
                  {["Excellent", "Average", "Poor"].map((o) => (
                    <button key={o} type="button" onClick={() => set("condition", o)} className={`rounded-xl border-2 py-3 text-sm font-bold transition-all ${data.condition === o ? "border-brand-navy bg-brand-navy text-white" : "border-brand-line hover:border-brand-navy/40"}`} data-testid={`condition-${o.toLowerCase()}`}>{o}</button>
                  ))}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Service log book?"><YN name="service_logbook" value={data.service_logbook} onChange={set} testid="yn-logbook" /></Field>
                <Field label="Service history" required error={errors.service_history}>
                  <select value={data.service_history} onChange={(e) => set("service_history", e.target.value)} className={inputCls} data-testid="input-servicehistory">
                    <option value="">Select</option>
                    {["Full", "Partial", "None"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-brand-muted mb-1.5">Photos (optional, up to 6)</label>
                <div className="flex flex-wrap gap-2" data-testid="photo-list">
                  {data.photos.map((p) => (
                    <div key={p} className="relative h-16 w-16 rounded-lg overflow-hidden border border-brand-line">
                      <img src={fileUrl(p)} alt="Car" className="h-full w-full object-cover" />
                      <button type="button" onClick={() => removePhoto(p)} className="absolute top-0.5 right-0.5 rounded-full bg-black/60 p-0.5" data-testid="remove-photo"><X className="h-3 w-3 text-white" /></button>
                    </div>
                  ))}
                  {data.photos.length < 6 && (
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-16 w-16 rounded-lg border-2 border-dashed border-brand-line flex items-center justify-center text-brand-muted hover:border-brand-gold hover:text-brand-gold transition-colors" data-testid="add-photo-btn">
                      {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
                    </button>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="photo-input" />
                <p className="mt-1 text-xs text-brand-muted">Photos help us give a faster, more accurate quote.</p>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Name" required error={errors.name}><input value={data.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={inputCls} data-testid="input-name" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Phone" required error={errors.phone}><input value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="04xx xxx xxx" inputMode="tel" className={inputCls} data-testid="input-phone" /></Field>
                <Field label="Email" error={errors.email}><input value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" inputMode="email" className={inputCls} data-testid="input-email" /></Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Suburb / Postcode" required error={errors.suburb}><input value={data.suburb} onChange={(e) => set("suburb", e.target.value)} placeholder="e.g. Dandenong 3175" className={inputCls} data-testid="input-suburb" /></Field>
                <Field label="Preferred contact time">
                  <select value={data.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} className={inputCls} data-testid="input-time">
                    <option value="">Any time</option>
                    {["Morning", "Afternoon", "Evening"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-6 flex items-center gap-3">
        {step > 0 && (
          <button type="button" onClick={back} className="inline-flex items-center gap-1 rounded-full border-2 border-brand-line px-5 py-3 font-bold text-brand-navy hover:bg-brand-surface transition-colors" data-testid="wizard-back">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
        )}
        {step < 2 ? (
          <button type="button" onClick={next} className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-brand-gold px-6 py-3.5 font-extrabold text-brand-navy hover:bg-brand-goldDark hover:-translate-y-0.5 transition-all" data-testid="wizard-next">
            Continue <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={submitting} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy px-6 py-3.5 font-extrabold text-white hover:bg-brand-navy2 hover:-translate-y-0.5 transition-all disabled:opacity-60" data-testid="wizard-submit">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />} Get my price
          </button>
        )}
      </div>
      <p className="mt-3 text-center text-xs text-brand-muted">No obligation. Free valuation. We never share your details.</p>
    </div>
  );
}
